import type { SpeakerLabel, Utterance } from './audio/utterance'
import { UtteranceEventType, type TeammateRealtimeEvent } from './realtime/teammateRealtimeController'

/**
 * One transcript item: the text of one utterance.
 * With speaker attribution, an item can exist before OpenAI knows it: speech start creates it, to reserve its place.
 */
interface TranscriptItem {
  /** The OpenAI connection of the item. Two sessions can use the same item ID, so the ID alone is not unique. */
  connectionId?: string
  /** OpenAI item ID */
  id?: string
  /** The utterance, with its position in the captured audio. Speaker attribution sorts the items by it. */
  utterance?: Utterance
  speaker?: SpeakerLabel
  text: string
  /** The text is final: it arrived complete, the transcription failed, or the item can no longer complete. */
  final: boolean
}

/** Formats a speaker label in the widget's display language. */
type LabelFormatter = (speaker: SpeakerLabel) => string

/**
 * Builds the transcript text of one listening session from OpenAI transcription events.
 * The text has two parts: saved text, which goes into the Action Card,
 * and interim text, which the widget only shows while OpenAI still works on it.
 *
 * Ordinary transcription (one OpenAI session): when the final text of an utterance arrives, it is saved at once.
 *
 * Speaker attribution (two sessions: microphone "Me" and system audio "Remote"): the lines get speaker labels.
 * The two sessions can return their texts in a different order than people spoke:
 * a short remote answer can be ready before the long local question. So the lines are sorted by speech start.
 * When speech starts, an empty item reserves its place. When OpenAI confirms the commit, the item gets its OpenAI ID,
 * and its text then fills the reserved place.
 * Text is saved only up to the first item without final text, so a late text can still go before later lines.
 * For overlapping speech, the order is approximate. One item is never split between speakers.
 */
export class TeammateTranscript {
  private items: TranscriptItem[] = []

  /** Start with the widget's saved transcript. `speakerAttribution` selects the mode with speaker labels. */
  constructor(
    private saved = '',
    private readonly speakerAttribution = false,
  ) {}

  /** Apply one event: a speech start, an OpenAI commit confirmation, new text, a failure, or a new session. */
  accept(event: TeammateRealtimeEvent) {
    // With speaker attribution, only the two speaker sessions have a transcript. Ignore the playbook-filling session.
    if (this.speakerAttribution && !event.connectionId) return
    switch (event.type) {
      case 'session.created':
        this.replaceSession(event)
        break
      case UtteranceEventType.started:
      case 'input_audio_buffer.committed':
        this.itemFor(event)
        break
      case 'conversation.item.input_audio_transcription.delta': {
        const item = this.itemFor(event)
        if (!item.final) item.text += event.delta ?? ''
        break
      }
      case 'conversation.item.input_audio_transcription.completed':
        this.complete(this.itemFor(event), event.transcript ?? '')
        break
      case 'conversation.item.input_audio_transcription.failed':
      case UtteranceEventType.empty:
        // No text will come. Release the reserved place, so that the later lines can be saved.
        this.complete(this.itemFor(event), '')
        break
    }
  }

  /**
   * Append all available text to the saved transcript when listening stops, including words without a final result.
   * Use the widget's current localized speaker labels.
   */
  finish(label: LabelFormatter) {
    this.saved = this.join(this.saved, this.format(this.items, label))
    this.items = []
  }

  /** Return the saved text and the interim text. Use the current localized speaker labels. */
  snapshot(label: LabelFormatter) {
    const firstPending = this.items.findIndex(item => !item.final)
    const finalCount = firstPending < 0 ? this.items.length : firstPending
    return {
      transcript: this.join(this.saved, this.format(this.items.slice(0, finalCount), label)),
      interim: this.format(this.items.slice(finalCount), label),
    }
  }

  /**
   * Handle a new OpenAI session, for example after a reconnect. The new session cannot complete the old items.
   * Ordinary transcription saves their text as it is.
   * Speaker attribution marks only the reconnected speaker's old items as final. The other speaker is not affected.
   */
  private replaceSession(event: TeammateRealtimeEvent) {
    if (!this.speakerAttribution) {
      // Ordinary items have no speaker labels.
      this.finish(speaker => speaker)
      return
    }
    for (const item of this.items) {
      if (item.speaker === event.speaker && item.connectionId !== event.connectionId) item.final = true
    }
  }

  /**
   * Find or create the item of an event, by OpenAI ID or by utterance.
   * Text can arrive before OpenAI confirms the commit. It then goes into an item without an utterance at first.
   * The commit confirmation links the ID to the reserved place, and the early text moves there.
   */
  private itemFor(event: TeammateRealtimeEvent) {
    const inConnection = (item: TranscriptItem) => item.connectionId === event.connectionId
    const byId = event.item_id ? this.items.find(item => inConnection(item) && item.id === event.item_id) : undefined
    const byUtterance = event.utterance
      ? this.items.find(item => inConnection(item) && item.utterance?.id === event.utterance!.id)
      : undefined
    if (byUtterance && byId && byUtterance !== byId) {
      byUtterance.text = byId.text
      byUtterance.final = byId.final
      this.items = this.items.filter(item => item !== byId)
    }
    let item = byUtterance ?? byId
    if (!item) {
      item = { connectionId: event.connectionId, text: '', final: false }
      this.items.push(item)
    }
    if (event.item_id) item.id = event.item_id
    if (event.utterance) item.utterance = event.utterance
    if (event.speaker) item.speaker = event.speaker
    if (this.speakerAttribution) {
      // Items without an utterance stay at the end until OpenAI confirms their commit.
      const position = (value: TranscriptItem) => value.utterance?.firstSpeechFrame ?? Number.MAX_SAFE_INTEGER
      this.items.sort((first, second) => position(first) - position(second))
    }
    return item
  }

  /**
   * Set the final text of an item.
   * In ordinary transcription, save it at once. The item stays, empty, so that a late event for it cannot add the text again.
   */
  private complete(item: TranscriptItem, text: string) {
    if (!this.speakerAttribution && item.final) return
    item.text = text
    item.final = true
    if (!this.speakerAttribution) {
      this.saved = this.join(this.saved, this.format([item], speaker => speaker))
      item.text = ''
    }
  }

  private join(first: string, second: string) {
    return [first, second].filter(Boolean).join('\n')
  }

  /**
   * Format items as text lines: one line for each utterance, with the speaker label if there is one.
   * Each line keeps its label, also when the speaker does not change, so that every line can be read on its own.
   */
  private format(items: TranscriptItem[], label: LabelFormatter) {
    return items
      .map(item => ({ speaker: item.speaker, text: item.text.trim() }))
      .filter(line => line.text)
      .map(line => (line.speaker ? `${label(line.speaker)}: ${line.text}` : line.text))
      .join('\n')
  }
}
